import type { DirectoryLog } from "@hydeck/mdt-contracts";
import { createEffect, createSignal, For, Show } from "solid-js";
import { apiRequest } from "../core/api";
import { currentUser } from "../core/session";
import { RichTextEditor } from "./RichTextEditor";

type HistoryKind = "incident" | "report";

export function HistoryModal(props: {
  kind: HistoryKind;
  id: number;
  categoryId?: number;
  open: boolean;
  canRevert?: boolean;
  onClose: () => void;
  onRevert?: (description: string) => void;
}) {
  const [entries, setEntries] = createSignal<DirectoryLog[]>([]);
  const [selected, setSelected] = createSignal<DirectoryLog | null>(null);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal("");
  let requestId = 0;

  createEffect(() => {
    if (!props.open) {
      setEntries([]);
      setSelected(null);
      setError("");
      return;
    }

    const currentRequest = ++requestId;
    setLoading(true);
    setError("");
    setSelected(null);
    void apiRequest<DirectoryLog[]>(historyPath(props), {
      token: currentUser()?.accessToken,
    })
      .then((value) => {
        if (currentRequest === requestId) setEntries(value);
      })
      .catch((reason) => {
        if (currentRequest === requestId) {
          setEntries([]);
          setError(reason instanceof Error ? reason.message : "Could not load history");
        }
      })
      .finally(() => {
        if (currentRequest === requestId) setLoading(false);
      });
  });

  const select = async (entry: DirectoryLog) => {
    setSelected(entry);
    try {
      const detail = await apiRequest<DirectoryLog>(historyDetailPath(props, entry.id), {
        token: currentUser()?.accessToken,
      });
      setSelected(detail);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not load history entry");
    }
  };

  const actorName = (entry: DirectoryLog) => {
    const named = `${entry.firstName ?? ""} ${entry.lastName ?? ""}`.trim();
    return named || entry.actor || "Unknown";
  };

  return (
    <Show when={props.open}>
      <div class="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-label="History">
        <div class="flex max-h-[90vh] w-full max-w-4xl flex-col rounded border border-white-11 bg-gray-gradient p-4 shadow-green-gradient">
          <div class="flex items-center justify-between border-b border-white-11 pb-3">
            <div>
              <p class="roboto700 text-2xl">History</p>
              <p class="text-xs text-white-55">Review previous saved versions</p>
            </div>
            <button type="button" class="h-9 w-9 bg-red-gradient" aria-label="Close history" onClick={props.onClose}>×</button>
          </div>

          <Show when={error()}>
            <p class="mt-3 bg-red-900/40 p-2 text-sm text-red-200">{error()}</p>
          </Show>

          <div class="mt-4 grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(16rem,30%)_1fr]">
            <div class="min-h-0 overflow-y-auto bg-white-05 p-2">
              <Show when={!loading()} fallback={<p class="p-3 text-sm text-white-55">Loading history...</p>}>
                <Show when={entries().length > 0} fallback={<p class="p-3 text-sm text-white-55">No history entries.</p>}>
                  <div class="flex flex-col gap-2">
                    <For each={entries()}>
                      {(entry) => (
                        <button
                          type="button"
                          class={`w-full bg-gray-gradient p-3 text-left hover:bg-green-gradient ${selected()?.id === entry.id ? "ring-1 ring-green-400" : ""}`}
                          onClick={() => void select(entry)}
                        >
                          <p class="text-sm">{actorName(entry)}</p>
                          <p class="text-xs text-white-55">{formatTimestamp(entry.createdAt)}</p>
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
              </Show>
            </div>

            <div class="min-h-0 overflow-y-auto bg-white-05 p-3">
              <Show when={selected()} fallback={<p class="text-sm text-white-55">Select a history entry.</p>}>
                {(entry) => (
                  <div class="flex min-h-0 flex-col gap-3">
                    <div class="flex items-center justify-between gap-3 border-b border-white-11 pb-2">
                      <div>
                        <p class="text-lg">{actorName(entry())}</p>
                        <p class="text-xs text-white-55">{formatTimestamp(entry().createdAt)}</p>
                      </div>
                      <Show when={props.canRevert && entry().description}>
                        <button
                          type="button"
                          class="bg-green-gradient px-3 py-1 text-xs"
                          onClick={() => props.onRevert?.(entry().description!)}
                        >
                          Revert
                        </button>
                      </Show>
                    </div>
                    <Show
                      when={entry().description}
                      fallback={<p class="whitespace-pre-wrap text-sm text-white-70">{entry().details || "No saved description."}</p>}
                    >
                      <RichTextEditor
                        value={entry().description!}
                        resetKey={entry().id}
                        disabled
                        roomName={`reporthistory-${entry().id}`}
                        onChange={() => undefined}
                      />
                    </Show>
                  </div>
                )}
              </Show>
            </div>
          </div>
        </div>
      </div>
    </Show>
  );
}

function historyPath(props: { kind: HistoryKind; id: number; categoryId?: number }) {
  return props.kind === "incident"
    ? `/incidents/${props.id}/history`
    : `/reports/${props.categoryId}/${props.id}/history`;
}

function historyDetailPath(props: { kind: HistoryKind; id: number; categoryId?: number }, historyId: number) {
  return `${historyPath(props)}/${historyId}`;
}

function formatTimestamp(value: string) {
  return new Date(value).toUTCString();
}
